import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./Inventory.css";
import { backend, API_URL } from "../../config";
import {
  Container,
  CssBaseline,
  Grid,
  IconButton,
  Typography,
  Button,
  TextField,
  Pagination,
  Checkbox,
  FormControlLabel,
  debounce,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  TableRows as TableRowsIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import { Link } from "react-router-dom";

import CardItem from "../../components/Cards/Items/Items";
import ItemDialog from "../../components/Dialogs/Items/Items";
// import InfiniteTable from "./../../components/InfiniteTable/InfiniteTable";

import { useSelector } from "react-redux";

const columnsConfig = {
  inventory: [
    { id: "upc", editable: "/product", editablePk: "upc", label: "UPC" },
    { id: "item_code", label: "Item Code" },
    {
      id: "url",
      label: "Image",
    },
    // { id: "upc", label: "Code" },
    {
      id: "name",
      label: "Name",
    },
    {
      id: "display_unit_price",
      label: "Unit",
    },
    {
      id: "display_case_price",
      label: "Case",
    },
    {
      id: "stock_level",
      label: "Inventory stock",
    },
    {
      id: "combined_stock_level",
      label: "Combined stock",
    },
    {
      id: "category",
      label: "Department",
      isCategory: true,
    },
  ],
  itemcodes: [
    {
      id: "item_code",
      editable: "/itemcodes",
      editablePk: "item_code",
      label: "Code",
    },
    {
      id: "url",
      label: "Image",
    },
    {
      id: "name",
      label: "Name",
    },
  ],
};

const imageTypes = [".jpg", ".jpeg", ".png"];

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: 440,
    maxHeight: "75vh",
  },
  tableBody: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export const TableBase = (props) => {
  const {
    columns,
    items,
    size = null,
    flattenedCategories = [],
    updatingProduct = null,
    handleProductCategoryUpdate,
  } = props;

  const classes = useStyles();
  const currentUserStore = useSelector((state) => state.current_user);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(null);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });

    return stabilizedThis.map((el) => el[0]);
  };

  const cellFormat = (value, row, column, rowIndex = null) => {
    if (column.isCategory) {
      const selectedCategory = flattenedCategories.find(
        (cat) => cat.id === row.category?.id,
      );

      return (
        <Autocomplete
          size="small"
          sx={{ minWidth: 150 }}
          options={flattenedCategories}
          getOptionLabel={(option) => option.name || ""}
          getOptionDisabled={(option) => !option.active}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.id}
              style={{
                paddingLeft: `${16 + option.depth * 16}px`,
                color:
                  option.depth === 0
                    ? "#000"
                    : option.depth === 1
                      ? "#333"
                      : "#666",
                fontSize: `${13 - option.depth * 0.5}px`,
                fontWeight:
                  option.depth === 0 ? 600 : option.depth === 1 ? 500 : 400,
              }}
            >
              <span
                style={{
                  marginRight: 6,
                  opacity: 0.6,
                  fontWeight: 400,
                }}
              >
                {option.depth === 1 ? "→ " : option.depth === 2 ? "↳ " : ""}
              </span>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select category" size="small" />
          )}
          value={selectedCategory || null}
          onChange={(e, newValue) => handleProductCategoryUpdate(row, newValue)}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          disabled={updatingProduct === row.upc}
        />
      );
    }

    switch (typeof value) {
      case "string":
        const dateVal = new Date(value);
        const containsLetter = value.match(/([A-Za-z])/g);
        if (
          !isNaN(dateVal.getTime()) &&
          containsLetter &&
          (!containsLetter.length ||
            (value.includes("T") && value.includes(":")))
        ) {
          return dateVal.toISOString().split("T")[0];
        } else if (
          value.indexOf("https://") === 0 ||
          value.indexOf("http://") === 0
        ) {
          const isImg = imageTypes.some((itype) =>
            value.toLocaleLowerCase().indexOf(itype),
          );

          if (!isImg) {
            return value;
          }

          const displayImageComp = (
            <Tooltip title="View image">
              <IconButton
                aria-label="view image"
                onClick={() => {
                  setSelectedItem(row);
                  setOpenItemDialog(true);
                }}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          );

          return displayImageComp;
        } else {
          return value;
        }
      case "number":
        if (column.id.includes("price") || column.id.includes("total")) {
          return "$" + value.toFixed(2);
        } else {
          return value;
        }
      case "boolean":
        return value.toString();
      case "object":
        if (value === null) return "N/A";

        if (column.hasOwnProperty(column.innerId)) {
          if (!value[column.innerId]) return "N/A";

          return value[column.innerId];
        }

        return value.hasOwnProperty(column.id) ? value[column.id] : "N/A";
      default:
        return value;
    }
  };

  const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort } = props;

    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {columns
            .filter((headCell) => {
              let visible = true;

              if (headCell.hasOwnProperty("visibility_deny_rid")) {
                visible = !headCell.visibility_deny_rid.includes(
                  currentUserStore.role_id,
                );
              }

              if (headCell.hasOwnProperty("visibility_allow_rid")) {
                visible = headCell.visibility_allow_rid.includes(
                  currentUserStore.role_id,
                );
              }

              return visible;
            })
            .map((headCell, index) => (
              <TableCell
                key={index}
                align={headCell.numeric ? "right" : "left"}
                sortDirection={orderBy === headCell.id ? order : false}
                style={{
                  width: ["url", "_acceptable_substitutions"].includes(
                    headCell.id,
                  )
                    ? 40
                    : "inherit",
                  padding: ["url", "_acceptable_substitutions"].includes(
                    headCell.id,
                  )
                    ? 0
                    : "auto",
                }}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <>
      <TableContainer
        className={classes.container}
        style={{ minHeight: size === null ? "inherit" : size }}
      >
        <Table stickyHeader>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(items, getComparator(order, orderBy)).map(
              (row, rowIndex) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={rowIndex}
                  className={`${classes.tableBody}${
                    row.active === false ? " iInactive" : ""
                  }`}
                >
                  {columns
                    .filter((headCell) => {
                      let visible = true;

                      if (headCell.hasOwnProperty("visibility_deny_rid")) {
                        visible = !headCell.visibility_deny_rid.includes(
                          currentUserStore.role_id,
                        );
                      }

                      if (headCell.hasOwnProperty("visibility_allow_rid")) {
                        visible = headCell.visibility_allow_rid.includes(
                          currentUserStore.role_id,
                        );
                      }

                      return visible;
                    })
                    .map((column, columnIndex) => (
                      <TableCell
                        key={rowIndex + "-" + columnIndex}
                        style={{
                          width: ["url", "_acceptable_substitutions"].includes(
                            column.id,
                          )
                            ? 40
                            : "inherit",
                          padding: [
                            "url",
                            "_acceptable_substitutions",
                          ].includes(column.id)
                            ? 0
                            : "auto",
                        }}
                      >
                        {column.editable && column.editablePk ? (
                          <Link
                            to={{
                              pathname: `${column.editable}/${
                                row[column.editablePk]
                              }`,
                              state: { data: row },
                            }}
                            {...(column?.target
                              ? {
                                  target: column.target,
                                }
                              : undefined)}
                          >
                            {row[column.id]}
                          </Link>
                        ) : (
                          cellFormat(row[column.id], row, column, rowIndex)
                        )}
                      </TableCell>
                    ))}
                </TableRow>
              ),
            )}
            {!items.length && (
              <TableRow style={{ height: 53 }}>
                <TableCell colSpan={columns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ItemDialog
        data={selectedItem}
        openItemDialog={openItemDialog}
        setOpenItemDialog={setOpenItemDialog}
      />
    </>
  );
};

const Inventory = (props) => {
  const isInventory = props.location.pathname.includes("inventory");

  const columns = isInventory
    ? columnsConfig.inventory
    : columnsConfig.itemcodes;

  const endpointURL = isInventory ? "products" : "itemcodes";

  const [itemsPerPage, setItemsPerPage] = useState(100); // todo: make it UI customizable
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0.0);
  const [view, setView] = useState(1);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeNonprimary, setIncludeNonprimary] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [flattenedCategories, setFlattenedCategories] = useState([]);
  const [updatingProduct, setUpdatingProduct] = useState(null);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [companyLocationSelected, setCompanyLocationSelected] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseSelected, setWarehouseSelected] = useState(null);

  const getProducts = useMemo(
    () => async () => {
      let urlParam = "";

      urlParam =
        urlParam + page > 1
          ? `limit=${itemsPerPage}&offset=${(page - 1) * itemsPerPage}`
          : "";

      urlParam =
        urlParam +
        (query.length > 0
          ? `${urlParam === "" ? "" : "&"}search=${query}`
          : "");

      urlParam =
        urlParam +
        `${urlParam === "" ? "" : "&"}include_inactive=${includeInactive}`;

      urlParam =
        urlParam +
        `${urlParam === "" ? "" : "&"}include_nonprimary=${includeNonprimary}`;

      if (companyLocationSelected?.company?.id) {
        urlParam =
          urlParam +
          `${urlParam === "" ? "" : "&"}company_location=${companyLocationSelected.company.id}`;
      }

      if (warehouseSelected?.id) {
        urlParam =
          urlParam +
          `${urlParam === "" ? "" : "&"}warehouse=${warehouseSelected.id}`;
      }

      await backend
        .get(`${API_URL}${endpointURL}?${urlParam}`)
        .then(({ data }) => {
          const dataMap = data.results.map((item) => ({
            ...item,
            unit_in_cart: 0,
            case_in_cart: 0,
            added_in_cart: false,
            added_through_sub: false,
          }));

          const pageCountCalc = data.count / itemsPerPage;
          setPageCount(+pageCountCalc.toFixed());
          setProducts(dataMap);
          setProductsLoaded(true);
        });
    },
    [
      page,
      itemsPerPage,
      query,
      includeInactive,
      includeNonprimary,
      endpointURL,
      companyLocationSelected,
      warehouseSelected,
    ],
  );

  useEffect(() => {
    getProducts();
  }, [
    itemsPerPage,
    page,
    query,
    includeInactive,
    includeNonprimary,
    getProducts,
    endpointURL,
  ]);

  useEffect(() => {
    const flattenCategories = (categories, depth = 0, parentPath = "") => {
      let flattened = [];

      categories.forEach((category) => {
        const fullPath = parentPath
          ? `${parentPath} > ${category.name}`
          : category.name;

        flattened.push({
          id: category.id,
          name: category.name,
          depth,
          fullPath,
          parent_id: category.parent_id || null,
          active: category.active,
        });

        if (category.children && category.children.length > 0) {
          flattened = [
            ...flattened,
            ...flattenCategories(category.children, depth + 1, fullPath),
          ];
        }
      });

      return flattened;
    };

    const categoriesGet = async () => {
      await backend
        .get(`${API_URL}productcategories?no_page=1`)
        .then(({ data }) => {
          const flattened = flattenCategories(data);
          setFlattenedCategories(flattened);
        })
        .catch((error) => {
          console.error("Error fetching categories:", error);
        });
    };

    const companyLocationsGet = async () => {
      await backend
        .get(`${API_URL}companylocations?no_page=1`)
        .then(({ data }) => {
          setCompanyLocations(data);
        });
    };

    const warehousesGet = async () => {
      await backend.get(`${API_URL}warehouses?no_page=1`).then(({ data }) => {
        const dataMap = data.map((item) => ({ id: item.id, name: item.name }));
        setWarehouses(dataMap);
      });
    };

    categoriesGet();
    companyLocationsGet();
    warehousesGet();
  }, []);

  // todo: remove when making itemsPerPage UI customizable
  useEffect(() => {
    if (!itemsPerPage) {
      setItemsPerPage(100);
    }
  }, [itemsPerPage]);

  const handleQuery = debounce((e) => {
    const { value } = e.target;
    setProducts([]);
    setProductsLoaded(false);
    setQuery(value);
  }, 300);

  const handleChangeIncludeInactive = (e) => {
    const { checked } = e.target;

    setIncludeInactive(checked);
  };

  const handleChangeIncludeNonprimary = (e) => {
    const { checked } = e.target;

    setIncludeNonprimary(checked);
  };

  const handlePaginationChange = (e, value) => {
    setProducts([]);
    setProductsLoaded(false);
    setPage(value);
  };

  const handleClickCardView = () => {
    setView(1);
  };

  const handleClickTableView = () => {
    setView(2);
  };

  const handleProductCategoryUpdate = async (product, selectedCategory) => {
    if (!selectedCategory) return;

    setUpdatingProduct(product.upc);

    try {
      const updateData = {
        category_id: selectedCategory.id,
      };

      await backend.patch(`${API_URL}productedit/${product.upc}`, updateData);

      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          p.upc === product.upc ? { ...p, category: selectedCategory } : p,
        ),
      );
    } catch (error) {
      console.error("Error updating product category:", error);
    } finally {
      setUpdatingProduct(null);
    }
  };

  const handleWarehouseUpdate = (newValue) => {
    setWarehouseSelected(newValue || null);
  };

  const handleCompanyLocationUpdate = (newValue) => {
    setCompanyLocationSelected(newValue || null);
  };

  return (
    <Fragment>
      <CssBaseline />
      <Container maxWidth="lg" className="container">
        <Grid container spacing={2}>
          <Grid item xs={true} sm={true}>
            <Typography variant="h6" component="div">
              {isInventory ? `Inventory` : `Item Codes`}
            </Typography>
          </Grid>
          <Grid item xs={"auto"} textAlign="right">
            {isInventory ? (
              <>
                <Link
                  to="/product/add"
                  style={{ marginRight: "10px", textDecoration: "none" }}
                  color="secondary"
                >
                  <Button variant="contained" color="primary">
                    <Typography variant="subtitle2" className="text-white">
                      Add Item
                    </Typography>
                  </Button>
                </Link>
                <Link
                  to="/inventory/bulk"
                  style={{ marginRight: "10px", textDecoration: "none" }}
                  color="secondary"
                >
                  <Button variant="contained" color="primary">
                    <Typography variant="subtitle2" className="text-white">
                      Import/Export
                    </Typography>
                  </Button>
                </Link>
                <Link
                  to="/inventory/sync"
                  style={{ marginRight: "10px", textDecoration: "none" }}
                  color="secondary"
                >
                  <Button variant="contained" color="primary">
                    <Typography variant="subtitle2" className="text-white">
                      Sync
                    </Typography>
                  </Button>
                </Link>
                <Link
                  to="/inventory/overrides"
                  style={{ marginRight: "10px", textDecoration: "none" }}
                  color="secondary"
                >
                  <Button variant="contained" color="primary">
                    <Typography variant="subtitle2" className="text-white">
                      Overrides
                    </Typography>
                  </Button>
                </Link>
                <Link
                  to="/inventory/categories"
                  style={{ marginRight: "10px", textDecoration: "none" }}
                  color="secondary"
                >
                  <Button variant="contained" color="primary">
                    <Typography variant="subtitle2" className="text-white">
                      Departments
                    </Typography>
                  </Button>
                </Link>
                <Link
                  to="/inventory/parstock"
                  style={{ marginRight: "10px", textDecoration: "none" }}
                  color="secondary"
                >
                  <Button variant="contained" color="primary">
                    <Typography variant="subtitle2" className="text-white">
                      Par Stock
                    </Typography>
                  </Button>
                </Link>
              </>
            ) : (
              <></>
            )}
            <IconButton onClick={handleClickCardView}>
              <DashboardIcon className={view === 1 ? "activeView" : ""} />
            </IconButton>
            <IconButton onClick={handleClickTableView}>
              <TableRowsIcon className={view === 2 ? "activeView" : ""} />
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={true} sm={true}>
                <TextField
                  fullWidth
                  autoFocus
                  key="search-text"
                  size="small"
                  id="search-text"
                  variant="outlined"
                  label="Search "
                  name="search-text"
                  defaultValue={query}
                  onChange={handleQuery}
                />
              </Grid>
              {isInventory ? (
                <>
                  <Grid item xs={"auto"} sm={"auto"}>
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 200 }}
                      options={companyLocations}
                      getOptionLabel={(option) => option.name || ""}
                      getOptionDisabled={(option) => !option.active}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Company Location"
                          size="small"
                        />
                      )}
                      value={companyLocationSelected || null}
                      onChange={(e, newValue) =>
                        handleCompanyLocationUpdate(newValue)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      disabled={companyLocations.length === 0}
                    />
                  </Grid>
                  <Grid item xs={"auto"} sm={"auto"}>
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 200 }}
                      options={warehouses}
                      getOptionLabel={(option) => option.name || ""}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Warehouse"
                          size="small"
                        />
                      )}
                      value={warehouseSelected || null}
                      onChange={(e, newValue) =>
                        handleWarehouseUpdate(newValue)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      disabled={warehouses.length === 0}
                    />
                  </Grid>
                  <Grid item xs={"auto"} sm={"auto"}>
                    <FormControlLabel
                      value="start"
                      control={
                        <Checkbox
                          name="include_inactive"
                          value={includeInactive}
                          checked={includeInactive}
                          onChange={handleChangeIncludeInactive}
                          color="primary"
                        />
                      }
                      label="Show Inactive"
                      labelPlacement="end"
                    />
                    <br />
                    <FormControlLabel
                      value="start"
                      control={
                        <Checkbox
                          name="include_nonprimary"
                          value={includeNonprimary}
                          checked={includeNonprimary}
                          onChange={handleChangeIncludeNonprimary}
                          color="primary"
                        />
                      }
                      label="Show Non Primary"
                      labelPlacement="end"
                    />
                  </Grid>
                </>
              ) : (
                <></>
              )}
              <Grid item xs={12} />
              {products.length ? (
                <>
                  {pageCount > 1 ? (
                    <Grid item xs={12} sm={12} textAlign="right">
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handlePaginationChange}
                        style={{ display: "inline-block" }}
                      />
                    </Grid>
                  ) : (
                    <></>
                  )}
                  {view === 1 ? (
                    products.map((item, index) => (
                      <Grid item xs={12} sm={4} md={3} key={index}>
                        <CardItem
                          {...props}
                          type={isInventory ? 2 : 3}
                          data={item}
                          total={total}
                          setTotal={setTotal}
                          setSelectedItem={setSelectedItem}
                          setOpenItemDialog={setOpenItemDialog}
                          reloadable={true}
                        />
                      </Grid>
                    ))
                  ) : (
                    <TableBase
                      columns={columns}
                      items={products}
                      flattenedCategories={flattenedCategories}
                      updatingProduct={updatingProduct}
                      handleProductCategoryUpdate={handleProductCategoryUpdate}
                    />
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="h6" align="center">
                    {!productsLoaded
                      ? "Loading products"
                      : "No products found with the provided search criteria"}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <ItemDialog
        data={selectedItem}
        openItemDialog={openItemDialog}
        setOpenItemDialog={setOpenItemDialog}
      />
    </Fragment>
  );
};

export default Inventory;
